/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useRef } from 'react'
import { Message, ChatFlowHandler, Repo } from '../types'
import { defaultChatFlow } from '../utils/defaultChatFlow'
import { useRouter } from 'next/navigation'
import {
  imageGenerationChatFlow,
  ImageGenerationState
} from '../utils/imageGenerationChatFlow'
import {
  updatePageContext,
  savePageMessageToThread,
  getChatThreads,
  createNewChatThread,
  savePageMessage
} from '@/lib/message-service'
import {
  BrandRepositoryResponse,
  fetchBrandRepositories
} from '@/lib/brand-repo-services'
import { saveCustomPage } from '@/lib/pages-service';

interface ChatMessage {
  message: string
  text: string
  sender: string
  editMode: boolean
  timestamp: string
  choices?: Array<{ id: number; text: string }>
  variants?: Array<{ id: number; description: string; html: string }>
}

export const useChatbot = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialData: any,
  onComplete?: (content: string, pageName?: string) => void,
  onClose?: (open: boolean) => void,
  flowHandler?: ChatFlowHandler,
  setBlocksAnimationLoading?: (loading: boolean) => void,
  setStreamingProgress?: (progress: number) => void,
  setIsStreaming?: (isStreaming: boolean) => void,
  setIsGeneratingSectionPromptsExternal?: (isGenerating: boolean) => void
) => {
  const chatFlow = flowHandler || defaultChatFlow
  const router = useRouter()
  const [isTyping, setIsTyping] = useState(false)
  const [messages, setMessages] = useState<Message[]>(
    chatFlow.getInitialMessages(initialData)
  )
  const [heroVariants, setHeroVariants] = useState<Array<{
    id: number
    description: string
    html: string
  }> | null>(null)
  const [selectedVariantId, setSelectedVariantId] = useState<
    number | undefined
  >()
  const [selectedRepoId, setSelectedRepoId] = useState<string>()
  const [isGeneratingContent, setIsGeneratingContent] = useState(false)
  const [currentState, setCurrentState] = useState(
    chatFlow.getInitialState(initialData)
  )
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)
  // Track when a new page is being generated
  const [isGeneratingPage, setIsGeneratingPage] = useState(false)
  // Track available pages for the website
  const [availablePages, setAvailablePages] = useState<string[]>([])
  const [isGeneratingSectionPrompts, setIsGeneratingSectionPrompts] =
    useState(false)

  const [chatThreads, setChatThreads] = useState<any[]>([])
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null)
  const [showThreadHistory, setShowThreadHistory] = useState(false)

  const initialized = useRef(false)

  const handleChoiceSelect = async (choice: { id: number; text: string }) => {
    // Add the selected choice as a user message
    // setMessages(prev => [...prev, { text: choice.text, sender: 'user' }]);

    // Process the choice as user input
    await handleUserInput(choice.text)
  }

  const resetChat = () => {
    // Reset messages to initial state based on current mode
    setMessages(chatFlow.getInitialMessages(initialData))
    // // Reset other relevant state
    setCurrentState(chatFlow.getInitialState(initialData))
  }

  const sendBotMessage = async (
    text: string,
    options: {
      choices?: Array<{ id: number; text: string }>
      variants?: Array<{ id: number; description: string; html: string }>
      repos?: Repo[]
    } = {}
  ) => {
    setIsTyping(true)

    try {
      // Save bot message to thread
      if (!flowHandler && initialData.pageDetails?.landingPageId) {
        const result = await savePageMessageToThread(
          initialData.pageDetails.landingPageId,
          activeThreadId,
          'bot',
          text,
          initialData.isEditModeEnabled,
          options?.choices,
          options?.variants,
          options?.repos
        )

        // If no active thread ID exists yet, set it from the result
        if (result.success && !activeThreadId && result.threadId) {
          setActiveThreadId(result.threadId)
        }
      }

      // Add message to UI
      setMessages(prev => [...prev, { text, sender: 'bot', ...options }])
    } catch (error) {
      console.error('Error sending bot message:', error)
    } finally {
      setIsTyping(false)
    }
  }

  const handleRepoSelect = async (repo: Repo) => {
    setSelectedRepoId(repo.id)

    let result: any
    try {
      if (chatFlow.handleRepoSelection) {
        result = await chatFlow.handleRepoSelection(
          repo,
          currentState,
          sendBotMessage
        )

        if (result?.newState) {
          setCurrentState(result.newState)
        }
      }
    } catch (err) {
      console.error(err)
    }
  }

  const handleRepoIgnore = async () => {
    setSelectedRepoId('')

    let result: any
    try {
      if (chatFlow.handleRepoSelection) {
        result = await chatFlow.handleRepoSelection(
          undefined,
          currentState,
          sendBotMessage
        )

        if (result?.newState) {
          setCurrentState(result.newState)
        }
      }
    } catch (err) {
      console.error(err)
    }
  }

  const handleVariantSelect = async (variant: {
    id: number
    description: string
    html: string
  }) => {
    setSelectedVariantId(variant.id)

    try {
      setIsTyping(true)
      setIsGeneratingContent(true)
      setBlocksAnimationLoading?.(true)

      let result: any

      setIsGeneratingSectionPrompts(true)
      setIsGeneratingSectionPromptsExternal?.(true)

      if (chatFlow.handleVariantSelection) {
        result = await chatFlow.handleVariantSelection(
          variant,
          currentState,
          sendBotMessage,
          progress => {
            setStreamingProgress?.(progress)
            setCurrentState((prevState: any) => ({
              ...prevState,
              streamingProgress: progress
            }))
          },
          setIsStreaming,
          (isGenerating: boolean) => {
            setIsGeneratingSectionPrompts(isGenerating)
            setIsGeneratingSectionPromptsExternal?.(isGenerating)
          }
        )
      }

      setIsGeneratingSectionPrompts(false)
      setIsGeneratingSectionPromptsExternal?.(false)

      if (result?.newState) {
        setCurrentState(result.newState)
      }

      // Update available pages based on the API response
      if (result?.newState?.availablePages) {
        setAvailablePages(result.newState.availablePages)
      }

      if (result?.content && onComplete) {
        // Pass the home page content to parent component
        onComplete(result.content, 'home')
      }

      // Update the current state with the new one that includes generated pages
      setCurrentState(result.newState || currentState)

      if (result?.closeChat && onClose) {
        onClose(false)
      }

      setIsTyping(false)
      setIsGeneratingContent(false)
      setBlocksAnimationLoading?.(false)
    } catch (error) {
      console.error('Error processing variant selection:', error)
      setIsTyping(false)
      setIsGeneratingContent(false)
      setIsGeneratingSectionPrompts(false)
      setIsGeneratingSectionPromptsExternal?.(false)
      setBlocksAnimationLoading?.(false)
      await sendBotMessage(
        'I apologize, but I encountered an error. Please try again.'
      )
    }
  }

  const handleUserInput = async (input: string) => {
    if (!input.trim()) return

    // Add user message to chat
    setMessages(prev => [...prev, { text: input, sender: 'user' }])

    try {
      setIsTyping(true)
      if (!flowHandler && initialData.pageDetails?.landingPageId) {
        // await savePageMessage(
        //   initialData.pageDetails.landingPageId,
        //   'user',
        //   input,
        //   initialData.isEditModeEnabled
        // )

        const result = await savePageMessageToThread(
          initialData.pageDetails.landingPageId,
          activeThreadId,
          'user',
          input,
          initialData.isEditModeEnabled
        )

        // If no active thread ID exists yet, set it from the result
        if (result.success && !activeThreadId && result.threadId) {
          setActiveThreadId(result.threadId)
        }
      }

      // Check if this is a request to generate an additional page
      const lowerInput = input.toLowerCase().trim()
      const isPossiblePageRequest =
        currentState.conversationComplete && availablePages.length > 0

      if (isPossiblePageRequest) {
        // Check if the user is requesting to generate a specific page
        const requestedPage = availablePages.find(
          page =>
            lowerInput === page.toLowerCase() ||
            lowerInput === `generate ${page} page`.toLowerCase() ||
            lowerInput === `create ${page} page`.toLowerCase()
        )

        if (requestedPage && chatFlow.generateAdditionalPage) {
          setIsGeneratingPage(true)
          setBlocksAnimationLoading?.(true)

          // Create a new page first to get the page ID
          let newPageId = null;
          
          try {
            // Create an empty page with the requested page name
            const newPageResult = await saveCustomPage(
              initialData.pageDetails?.landingPageId,
              {}, // Empty content initially
              requestedPage,
              null, // No UTM params
              '',  // No URL
            );
            
            if (newPageResult?.success) {
              newPageId = newPageResult.data.page.pageId;
              
              // Update the currentState with the new page ID and name
              setCurrentState((prevState: any) => ({
                ...prevState,
                pageDetails: newPageResult.data.page
              }));
            }
          } catch (error) {
            console.error('Error creating new page:', error);
          }

          // if (onComplete) {
          //   // Pass empty content first to trigger page activation in the builder
          //   onComplete('', requestedPage);
          // }

          const result = await chatFlow.generateAdditionalPage(
            requestedPage,
            {
              ...currentState,
              pageDetails: {
                ...currentState.pageDetails,
                pageId: newPageId || currentState.pageDetails?.pageId
              }
            },
            sendBotMessage,
            progress => {
              setStreamingProgress?.(progress)
              setCurrentState((prevState: any) => ({
                ...prevState,
                streamingProgress: progress
              }))
            },
            setIsStreaming
          )

          // Update state with the new content
          if (result.content && onComplete) {
            onComplete(result.content, requestedPage)
          }

          // Update current state
          if (result.newState) {
            setCurrentState(result.newState)

            // Update available pages
            if (result.newState.availablePages) {
              setAvailablePages(result.newState.availablePages)
            }
          }

          setIsGeneratingPage(false)
          setBlocksAnimationLoading?.(false)
          setIsTyping(false)
          return
        }
      }

      // Regular flow (after initial prompt is set)
      const result = await chatFlow.handleUserInput(
        input,
        currentState,
        sendBotMessage,
        initialData,
        activeThreadId!,
        setIsTyping
      )

      setCurrentState(result.newState)

      if (result.variants) {
        setHeroVariants(result.variants)
      }

      if (result.navigateTo) {
        router.push(result.navigateTo)
      }

      // Check for available pages in the response
      if (result.newState?.availablePages) {
        setAvailablePages(result.newState.availablePages)
      }

      if (result.content && onComplete) {
        onComplete(result.content)

        // Execute any scripts after a small delay to ensure DOM is updated
        if (result.sectionScripts) {
          setTimeout(() => {
            try {
              // Create a function from the script content and execute it
              const scriptFunc = new Function(
                result.sectionScripts ? result.sectionScripts : ''
              )
              scriptFunc()
            } catch (error) {
              console.error('Error executing section scripts:', error)
            }
          }, 300) // A bit longer delay to ensure DOM has updated
        }
      }

      setIsTyping(false)
    } catch (error) {
      console.error('Error processing user input:', error)
      setIsTyping(false)
      await sendBotMessage(
        'I apologize, but I encountered an error. Please try again.'
      )
    }
  }

  const handleImageSelect = async (imageUrl: string) => {
    // Download the image
    const link = document.createElement('a')
    link.href = imageUrl
    link.download = 'generated-image.png'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  useEffect(() => {
    // Check if there's a prompt from URL in the initial data
    if (initialData?.promptFromUrl && !flowHandler) {
      // Process it automatically after a short delay to allow the UI to render
      setTimeout(() => {
        // This will trigger the flow to analyze this prompt
        handleUserInput(initialData.promptFromUrl)
      }, 500)
    }
  }, [])

  const createNewThread = async () => {
    if (!initialData.pageDetails?.landingPageId) return

    setIsLoadingMessages(true)

    try {
      const result = await createNewChatThread(
        initialData.pageDetails.landingPageId,
        initialData.isEditModeEnabled
      )

      if (result.success && result.threadId) {
        setActiveThreadId(result.threadId)

        // Fetch all threads to update the list
        const threadResult = await getChatThreads(
          initialData.pageDetails.landingPageId,
          initialData.isEditModeEnabled
        )

        if (threadResult.success) {
          setChatThreads(threadResult.data)

          // Find the newly created thread
          const newThread = threadResult.data.find(
            (thread: any) => thread.threadId === result.threadId
          )

          if (newThread) {
            // Set the messages from the new thread
            const formattedMessages = newThread.messages.map((msg: any) => ({
              text: msg.message,
              sender: msg.sender,
              timestamp: msg.timestamp,
              choices: msg.choices,
              variants: msg.variants,
              showVariants: msg.variants.length > 0,
              repos: msg.repos
            }))

            setMessages(formattedMessages)

            // Reset state for new thread
            setCurrentState(chatFlow.getInitialState(initialData))
          }
        }
      }
    } catch (error) {
      console.error('Error creating new thread:', error)
    } finally {
      setIsLoadingMessages(false)
    }
  }

  const loadChatThread = async (threadId: string) => {
    if (!initialData.pageDetails?.landingPageId) return

    setIsLoadingMessages(true)
    setShowThreadHistory(false)

    try {
      const result = await getChatThreads(
        initialData.pageDetails.landingPageId,
        initialData.isEditModeEnabled
      )

      if (result.success) {
        const thread = result.data.find((t: any) => t.threadId === threadId)

        if (thread) {
          setActiveThreadId(threadId)

          // Format messages for the selected thread
          const formattedMessages = thread.messages.map((msg: any) => ({
            text: msg.message,
            sender: msg.sender,
            timestamp: msg.timestamp,
            choices: msg.choices,
            variants: msg.variants,
            showVariants: msg.variants.length > 0,
            repos: msg.repos
          }))

          setMessages(formattedMessages)

          // Reconstruct state from thread messages
          const newState = reconstructStateFromMessages(
            thread.messages,
            initialData
          )
          setCurrentState(newState)

          // Set hero variants if present in thread
          const variantMessage = thread.messages.find(
            (msg: any) => msg.variants
          )
          if (variantMessage) {
            setHeroVariants(variantMessage.variants)

            // Check if a variant was selected
            const variantSelectionMessage = thread.messages.find((msg: any) =>
              msg.message.includes("You've selected variant")
            )

            if (variantSelectionMessage) {
              const variantIdMatch =
                variantSelectionMessage.message.match(/variant (\d+)/)
              if (variantIdMatch && variantIdMatch[1]) {
                const variantId = parseInt(variantIdMatch[1])
                setSelectedVariantId(variantId)
              }
            }
          }

          // Check for and set available pages
          const availablePagesMessage = thread.messages.find((msg: any) =>
            msg.message.includes('I can also create these additional pages')
          )

          if (availablePagesMessage) {
            const pagesMatch = availablePagesMessage.message.match(
              /additional pages for your website: ([^.]+)/
            )
            if (pagesMatch && pagesMatch[1]) {
              const pages = pagesMatch[1]
                .split(', ')
                .map((page: string) => page.trim().toLowerCase())
              setAvailablePages(pages)
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading thread:', error)
    } finally {
      setIsLoadingMessages(false)
    }
  }

  const reconstructStateFromMessages = (messages: any[], initialData: any) => {
    // Start with initial state from the chat flow
    const newState = chatFlow.getInitialState(initialData)

    // Immediately set pageDetails from initialData
    newState.pageDetails = {
      ...newState.pageDetails,
      ...initialData.pageDetails,
      pageContext: initialData.pageDetails?.pageContext || {}
    }

    

    // 1. Determine website type from messages
    const websiteTypeMessage = messages.find(
      (msg: any) =>
        msg.message.toLowerCase().includes('landing page') ||
        msg.message.toLowerCase().includes('single page') ||
        msg.message.toLowerCase().includes('multi page')
    )

    if (websiteTypeMessage) {
      if (websiteTypeMessage.message.toLowerCase().includes('landing page')) {
        newState.websiteType = 'landing'
      } else if (
        websiteTypeMessage.message.toLowerCase().includes('single page')
      ) {
        newState.websiteType = 'single'
      } else if (
        websiteTypeMessage.message.toLowerCase().includes('multi page')
      ) {
        newState.websiteType = 'multi'
      }
    }

    // 2. For multi-page websites, handle generated and available pages
    if (newState.websiteType === 'multi') {
      // Initialize tracking of generated pages
      newState.generatedPages = {}

      // Check for home page generation
      const homePageMessage = messages.find((msg: any) =>
        msg.message.includes("I've generated the home page")
      )
      if (homePageMessage) {
        // Mark home as generated
        newState.generatedPages['home'] = true
        newState.conversationComplete = true
        newState.currentPage = 'home'
        newState.isEditMode = true
        newState.currentEditRequest = null

        // Look for available pages message
        const availablePagesMessage = messages.find((msg: any) =>
          msg.message.includes('I can also create these additional pages')
        )

        if (availablePagesMessage) {
          const pagesMatch = availablePagesMessage.message.match(
            /additional pages for your website: ([^.]+)/
          )
          if (pagesMatch && pagesMatch[1]) {
            // Get all possible pages
            const allPages = pagesMatch[1]
              .split(', ')
              .map((page: string) => page.trim().toLowerCase())

            // Check which pages have been generated
            allPages.forEach((page: string) => {
              const pageGenerated = messages.some((msg: any) =>
                msg.message.toLowerCase().includes(`generated the ${page} page`)
              )

              if (pageGenerated) {
                newState.generatedPages[page] = true
              }
            })

            // Set available pages (not yet generated)
            const availablePages = allPages.filter(
              (page: string) => !newState.generatedPages[page]
            )

            newState.availablePages = availablePages
            setAvailablePages(newState.availablePages)
          }
        }
      }
    } else {
      // For landing or single page websites
      const generatedPageMessage = messages.find(
        (msg: any) =>
          msg.message.includes("I've generated the landing page") ||
          msg.message.includes("I've generated the page")
      )

      if (generatedPageMessage) {
        newState.conversationComplete = true
        newState.currentPage = 'home'
      }
    }

    // 3. Find user prompt from messages
    const userMessages = messages.filter((msg: any) => msg.sender === 'user')

    if (initialData.pageDetails?.pageContext?.originalPrompt) {
      // Prefer page context if available
      newState.userPrompt = initialData.pageDetails.pageContext.originalPrompt
      newState.originalPrompt =
        initialData.pageDetails.pageContext.originalPrompt
    } else if (userMessages.length >= 2) {
      // Second user message is typically the business description
      newState.userPrompt = userMessages[1].message
      newState.originalPrompt = userMessages[1].message
    } else if (userMessages.length === 1) {
      // Fall back to first message if only one exists
      newState.userPrompt = userMessages[0].message
      newState.originalPrompt = userMessages[0].message
    }

    // 4. Handle hero variants
    const variantMessage = messages.find((msg: any) => msg.variants)

    if (variantMessage && variantMessage.variants) {
      // Find if a variant was selected
      const variantSelectionMessage = messages.find((msg: any) =>
        msg.message.includes("You've selected variant")
      )

      if (variantSelectionMessage) {
        const variantIdMatch =
          variantSelectionMessage.message.match(/variant (\d+)/)

        if (variantIdMatch && variantIdMatch[1]) {
          const variantId = parseInt(variantIdMatch[1])

          // Find the selected variant
          const selectedVariant = variantMessage.variants.find(
            (v: any) => v.id === variantId
          )

          if (selectedVariant && selectedVariant.html) {
            newState.selectedHero = selectedVariant.html
          }
        }
      }
    }

    
    

    

    const repoSelectionMessage = messages.find(
      (msg: any) =>
        msg.message.includes("You've selected repo") ||
        msg.message.includes('No repository selected')
    )

    if (repoSelectionMessage) {
      newState.isRepoSelected = true
    }
    

    // 5. Set page context (sections, content) if available
    if (initialData.pageDetails?.pageContext) {
      newState.previousSections =
        initialData.pageDetails.pageContext.sectionPrompts || []
      newState.currentContent =
        initialData.pageDetails.pageContext.pageContent || ''
    }

    // 6. Set current page from initialData if available
    if (initialData.pageDetails?.pageName) {
      newState.currentPage = initialData.pageDetails.pageName.toLowerCase()
    }

    // 7. Set edit mode state
    if (initialData.isEditModeEnabled) {
      newState.isEditMode = true
      newState.isEditModeEnabled = true

      // Reset edit request to ensure edit UI is shown properly
      newState.currentEditRequest = null
    }

    return newState
  }

  const toggleThreadHistory = async () => {
    if (!initialData.pageDetails?.landingPageId) return

    if (!showThreadHistory) {
      try {
        // Always reload threads when opening the history panel
        const result = await getChatThreads(
          initialData.pageDetails.landingPageId,
          initialData.isEditModeEnabled
        )

        if (result.success) {
          // Check if we have data
          if (result.data && result.data.length > 0) {
            setChatThreads(result.data)
          } else {
            setChatThreads([])
          }
        } else {
          console.error('Failed to load chat threads')
          setChatThreads([])
        }
      } catch (error) {
        console.error('Error fetching thread history:', error)
        setChatThreads([])
      }
    }

    setShowThreadHistory(!showThreadHistory)
  }

  useEffect(() => {
    const initializeChat = async () => {
      if (initialized.current) return
      // if (!currentState.userPrompt) return // ❗ Wait for user to enter their prompt

      initialized.current = true
      // Check if the flowHandler is imageGenerationChatFlow
      const isImageGeneration = flowHandler === imageGenerationChatFlow
      if (isImageGeneration) {
        setIsTyping(true)
        await chatFlow.initialize(currentState, sendBotMessage)
        setIsTyping(false)
      } else {
        // For other flows, wait for user prompt
        if (!currentState.userPrompt) return
        setIsTyping(true)
        await chatFlow.initialize(currentState, sendBotMessage)
        setIsTyping(false)
      }
    }

    initializeChat()
  }, [])

  useEffect(() => {
    if (initialData.selectedFolderName) {
      setCurrentState((prev: ImageGenerationState) => ({
        ...prev,
        selectedFolderName: initialData.selectedFolderName
      }))
    }
  }, [initialData.selectedFolderName])

  useEffect(() => {
    const updatePageData = async () => {
      // Only proceed if we have page details and are in a multi-page website context
      if (initialData.pageDetails && currentState.websiteType === 'multi') {
        // Get the current page name from the page details
        const currentPageName = initialData.pageDetails.pageName?.toLowerCase()

        // Check if this is not the home page and we have the necessary data
        if (currentPageName && currentPageName !== 'home') {
          // Get the prompt either from analyzedData or from userPrompt
          const prompt =
            currentState.analyzedData?.user_prompt ||
            currentState.userPrompt ||
            initialData.pageDetails?.pageContext?.originalPrompt

          // Get the sections either from current state or from page context
          const sections =
            currentState.previousSections ||
            initialData.pageDetails?.pageContext?.sectionPrompts

          // Get the content either from current state or from page context
          const content =
            currentState.currentContent ||
            initialData.pageDetails?.pageContext?.pageContent

          // Only update if we have all necessary data
          if (prompt && sections && content) {
            try {
              // Update the page context for the new page
              const updateContext = await updatePageContext(
                initialData.pageDetails.pageId,
                prompt,
                sections,
                content
              )
              const pageContextResult = await initialData.pageDetails.pageId

              if (updateContext.success && pageContextResult.data) {
                // Update the current state with fresh data
                setCurrentState((prev: any) => ({
                  ...prev,
                  pageDetails: {
                    ...prev.pageDetails,
                    ...initialData.pageDetails,
                    pageContext: pageContextResult.data.pageContext || {}
                  },
                  // Preserve other important state properties
                  websiteType: prev.websiteType,
                  conversationComplete: prev.conversationComplete,
                  generatedPages: prev.generatedPages,
                  availablePages: prev.availablePages,
                  currentPage: currentPageName,
                  // Update content-related properties with fresh data
                  userPrompt: prompt,
                  previousSections: sections,
                  currentContent: content,
                  originalPrompt: prompt
                }))
              } else {
                console.error(
                  'Failed to fetch updated page context:',
                  pageContextResult.error
                )
              }
            } catch (error: any) {
              console.error(
                'Error during page context update:',
                error.message || error
              )
            }
          }
        }
      }
    }

    // First update the state with new page details
    setCurrentState((prev: any) => ({
      ...prev,
      pageDetails: initialData.pageDetails
    }))
    // Then handle page context update if needed
    if (!flowHandler && initialData?.pageDetails?.pageId) {
      updatePageData()
    }
  }, [initialData.pageDetails])

  useEffect(() => {
    const loadMessages = async () => {
      setIsLoadingMessages(true)
      try {
        // Fetch chat threads
        const result = await getChatThreads(
          initialData.pageDetails.landingPageId,
          initialData.isEditModeEnabled
        )

        if (result.success) {
          setChatThreads(result.data)

          // If we have threads, load the most recent one
          if (result.data && result.data.length > 0) {
            const latestThread = result.data[0] // Assuming threads are sorted by creation date
            setActiveThreadId(latestThread.threadId)

            // Format messages for the latest thread
            const formattedMessages = latestThread.messages.map((msg: any) => ({
              text: msg.message,
              sender: msg.sender,
              timestamp: msg.timestamp,
              choices: msg.choices,
              variants: msg.variants,
              showVariants: msg.variants.length > 0,
              repos: msg?.repos
            }))

            setMessages(formattedMessages)

            // Set hero variants if present in thread
            const variantMessage = latestThread.messages.find(
              (msg: any) => msg.variants.length !== 0
            )
            if (variantMessage) {
              setHeroVariants(variantMessage.variants)
            }

            // Reconstruct state from thread messages
            const newState = reconstructStateFromMessages(
              latestThread.messages,
              initialData
            )
            setCurrentState(newState)
          } else {
            // If no threads exist, create a new one
            await createNewThread()
          }
        } else {
          // If there was an error or no threads, create a new thread
          console.error('Failed to load threads, creating a new one')
          await createNewThread()
        }
      } catch (error) {
        console.error('Error loading messages:', error)
        await createNewThread()
      } finally {
        setIsLoadingMessages(false)
      }
    }

    if (!flowHandler && initialData.pageDetails?.pageId) {
      loadMessages()
    }
  }, [initialData.pageDetails?.landingPageId, initialData.isEditModeEnabled])

  useEffect(() => {
    // If using a custom flow handler (not the default flow), don't show loading state
    if (flowHandler || initialData?.promptFromUrl) {
      setIsLoadingMessages(false)
    }
  }, [flowHandler])

  return {
    isTyping,
    messages,
    handleUserInput,
    handleChoiceSelect,
    heroVariants,
    handleVariantSelect,
    selectedVariantId,
    isGeneratingContent,
    handleImageSelect,
    isGeneratingPage,
    isGeneratingSectionPrompts,
    availablePages,
    currentState,
    resetChat,
    isLoadingMessages,
    // New thread-related items
    createNewThread,
    loadChatThread,
    toggleThreadHistory,
    chatThreads,
    activeThreadId,
    showThreadHistory,
    setShowThreadHistory,
    handleRepoSelect,
    selectedRepoId,
    handleRepoIgnore
  }
}
